import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { useAuthStore } from "@/store/auth";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

const ERROR_MSG = "Failed to fetch user info";
const API_PATH = "/api/auth/me";
const USE_QUERY_KEY = API_PATH;

export function useUserInfo() {
  const { token, setUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const { handleError } = useToastHandler();

  return useQuery({
    enabled: !!token, // 没 token 就不请求
    queryKey: [USE_QUERY_KEY],
    //refetchInterval: 1000 * 60 * 5, // 每 5 分钟自动刷新一次
    refetchOnWindowFocus: true, // 当用户切回你的应用页面（如从其他标签页或应用返回），React Query 会自动重新获取一次数据，确保用户看到的是最新内容。
    refetchOnMount: true, // 组件挂载时：如果发现数据已经 stale 或者被 invalidated，就会重新请求数据。
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      try {
        const res = await api.get(API_PATH);

        if (import.meta.env.DEV) {
          console.log(
            `>>> React Hook Fetching .... [${USE_QUERY_KEY}]`,
            res.data
          );
        }

        if (typeof res.data !== "object") {
          throw new Error(ERROR_MSG + "(Invalid Format)");
        }
        if (res.data.error) {
          throw new Error(res.data.error);
        }
        if (!res.data.user) {
          throw new Error(ERROR_MSG + "(Invalid Data)");
        }
        setUser(res.data.user);
        return res.data.user;
      } catch (err: unknown) {
        let http_status = 0;
        if (err instanceof AxiosError && err.response?.status) {
          http_status = err.response.status;
        }
        if (http_status === 401 || http_status === 403) {
          handleError("Authentication failed. Please login again.", 3000);
          logout();
          navigate("/login");
          throw err;
        }

        // other errors:
        handleError(
          err instanceof Error ? err.message : ERROR_MSG,
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );

        throw err;
      }
    },
  });
}

export function useInvalidateUserInfo() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [USE_QUERY_KEY] });
  };
}

export function useRefetchUserInfo() {
  const { refetch } = useUserInfo();
  return refetch;
}
